<script setup>
import { computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import { WEEKDAYS, SERVICE_TIMES_MAX, SERVICE_DURATION_MIN, SERVICE_DURATION_MAX } from '@/components/Sites/siteTemplates'
import { audienceLabels, AUDIENCE_CHURCH } from '@/components/Sites/broadcastAudience'

// Rows of runtime `broadcastServiceTimes` (contract §2.2): `{ label, weekday,
// time, durationMinutes? }`. The parent owns the array and the validation;
// `errors[i]` is the message for row `i` (or null).
const props = defineProps({
	modelValue: {
		type: Array,
		required: true,
	},
	errors: {
		type: Array,
		default: () => [],
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	/** Broadcast audience preset: the row wording follows it. */
	audience: {
		type: String,
		default: AUDIENCE_CHURCH,
	},
})
const emit = defineEmits(['update:modelValue'])

const labels = computed(() => audienceLabels(props.audience))
const weekdayOptions = WEEKDAYS.map((d) => ({ value: d.value, text: d.text }))

const update = (i, patch) => emit('update:modelValue', props.modelValue.map((row, idx) => (idx === i ? { ...row, ...patch } : row)))
const remove = (i) => emit('update:modelValue', props.modelValue.filter((_, idx) => idx !== i))
const add = () => {
	if (props.modelValue.length >= SERVICE_TIMES_MAX) return
	// Default to Sunday morning after the last row, like most church schedules.
	const last = props.modelValue[props.modelValue.length - 1]
	emit('update:modelValue', [...props.modelValue, { label: '', weekday: last ? last.weekday : 0, time: last ? last.time : '10:00', durationMinutes: last?.durationMinutes ?? 90 }])
}
const onDuration = (i, value) => {
	const v = String(value ?? '').trim()
	update(i, { durationMinutes: v === '' ? null : Number(v) })
}
</script>

<template>
	<div class="flex flex-col gap-2" data-testid="service-times-editor">
		<!-- From 640px one header labels the columns; the per-field labels stay for assistive tech. -->
		<div v-if="modelValue.length" class="hidden sm:block" aria-hidden="true" data-testid="service-times-header">
			<div class="service-times-grid text-tiny font-medium text-surface-text-1">
				<span class="[grid-area:label]">Label</span>
				<span class="[grid-area:day]">Day</span>
				<span class="[grid-area:time]">Starts</span>
				<span class="[grid-area:duration]">Minutes</span>
			</div>
		</div>
		<div v-for="(row, i) in modelValue" :key="i" class="min-w-0 rounded-lg border border-surface-4 p-2.5 sm:rounded-none sm:border-0 sm:p-0" :data-testid="`service-time-row-${i}`">
			<div class="service-times-grid">
				<p class="mb-0 self-center text-tiny font-medium text-surface-7 [grid-area:title] sm:hidden">{{ labels.rowTitle }} {{ i + 1 }}</p>
				<div class="service-time-field min-w-0 [grid-area:label]">
					<label :for="`service-time-label-${i}`" class="mb-1.5 block text-tiny font-medium text-surface-text-1 sm:sr-only">Label</label>
					<FormInput :id="`service-time-label-${i}`" :model-value="row.label" :placeholder="labels.placeholder" input-classes="placeholder:text-surface-6" :maxlength="40" :disabled="disabled" @update:model-value="update(i, { label: $event })" />
				</div>
				<div class="service-time-field min-w-0 [grid-area:day]">
					<label :for="`service-time-weekday-${i}`" class="mb-1.5 block text-tiny font-medium text-surface-text-1 sm:sr-only">Day</label>
					<FormSelect :id="`service-time-weekday-${i}`" :model-value="row.weekday" :options="weekdayOptions" :disabled="disabled" @update:model-value="update(i, { weekday: Number($event) })" />
				</div>
				<div class="service-time-field min-w-0 [grid-area:time]">
					<label :for="`service-time-time-${i}`" class="mb-1.5 block text-tiny font-medium text-surface-text-1 sm:sr-only">Starts</label>
					<FormInput :id="`service-time-time-${i}`" :model-value="row.time" type="time" :disabled="disabled" @update:model-value="update(i, { time: $event })" />
				</div>
				<div class="service-time-field min-w-0 [grid-area:duration]">
					<label :for="`service-time-duration-${i}`" class="mb-1.5 block text-tiny font-medium text-surface-text-1 sm:sr-only">Minutes</label>
					<FormInput :id="`service-time-duration-${i}`" :model-value="row.durationMinutes ?? ''" type="number" placeholder="90" input-classes="placeholder:text-surface-6" :disabled="disabled" @update:model-value="onDuration(i, $event)" />
				</div>
				<FormButton is-icon type="danger" size="md" :title="`Remove ${labels.rowTitle.toLowerCase()}`" :aria-label="`Remove ${labels.rowTitle.toLowerCase()} ${i + 1}`" class="!size-11 justify-self-end [grid-area:remove]" :disabled="disabled" :data-testid="`service-time-remove-${i}`" @click="remove(i)">
					<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
				</FormButton>
			</div>
			<p v-if="errors[i]" class="mb-0 mt-1.5 text-tiny text-error" :data-testid="`service-time-error-${i}`">{{ errors[i] }}</p>
		</div>
		<p v-if="!modelValue.length" class="mb-0 rounded-lg bg-surface-3 px-3 py-2.5 text-tiny text-surface-7" data-testid="service-times-empty">
			{{ labels.empty }}
		</p>
		<div class="flex flex-wrap items-center gap-3">
			<FormButton type="secondary" size="sm" :disabled="disabled || modelValue.length >= SERVICE_TIMES_MAX" data-testid="service-time-add" @click="add">
				<icon-base class="mr-1" :width="12" :height="12"><icon-plus /></icon-base>
				{{ labels.add }}
			</FormButton>
			<span class="text-tiny text-surface-7">Up to {{ SERVICE_TIMES_MAX }} · duration {{ SERVICE_DURATION_MIN }}–{{ SERVICE_DURATION_MAX }} minutes, empty for the default.</span>
		</div>
	</div>
</template>

<style scoped>
/* Starts must fit "10:00 AM" plus the native picker icon at 16px (the mobile text size). */
.service-times-grid {
	display: grid;
	gap: 0.5rem 0.375rem;
	grid-template-columns: minmax(0, 1fr) 8rem 3.0625rem;
	grid-template-areas:
		'title title remove'
		'label label label'
		'day time duration';
	align-items: end;
}
@media (max-width: 389px) {
	.service-times-grid {
		grid-template-columns: minmax(0, 1fr) 3.5rem;
		grid-template-areas:
			'title remove'
			'label label'
			'day day'
			'time duration';
	}
}
@media (min-width: 640px) {
	.service-times-grid {
		column-gap: 0.75rem;
		grid-template-columns: minmax(8rem, 1.6fr) minmax(7.5rem, 1fr) minmax(9rem, 10rem) minmax(4.5rem, 5.5rem) 2.75rem;
		grid-template-areas: 'label day time duration remove';
		align-items: center;
	}
}
/* The rows sit on the card's surface, so a filled field alone does not read as an input. */
.service-time-field :deep(input),
.service-time-field :deep(select) {
	border: 1px solid var(--surface-5);
}
.service-time-field :deep(input:hover:not(:disabled)),
.service-time-field :deep(select:hover:not(:disabled)) {
	border-color: var(--surface-6);
}
.service-time-field :deep(input:focus),
.service-time-field :deep(select:focus) {
	border-color: var(--c-main);
	box-shadow: 0 0 0 1px var(--c-main);
}
/* Chromium leaves 24px before the picker icon. */
.service-time-field :deep(input[type='time']::-webkit-calendar-picker-indicator) {
	margin-inline-start: 0.25rem;
}
/* Three fields share ~300px on a phone. */
@media (max-width: 639px) {
	.service-time-field :deep(input[type='time']) {
		padding-inline: 0.5rem;
	}
	.service-time-field :deep(input[type='number']) {
		padding-inline: 0.5rem;
		-moz-appearance: textfield;
	}
	.service-time-field :deep(input[type='number']::-webkit-inner-spin-button),
	.service-time-field :deep(input[type='number']::-webkit-outer-spin-button) {
		-webkit-appearance: none;
		margin: 0;
	}
	.service-time-field :deep(select),
	.service-time-field :deep(select + span) {
		padding-left: 0.625rem;
		padding-right: 1.625rem;
	}
	.service-time-field :deep(select) {
		background-position: right 0.4375rem center;
	}
}
</style>
